// HubSpot Forms Submission API への送信ユーティリティ。
// カスタムUI（診断・各フォーム）の入力をHubSpotフォームへ送る。
// ※既存お問い合わせと同じ portalId を使用。フォームGUIDは下記で集中管理。

import { getHubSpotUtk } from "../analytics/hubspot";

const PORTAL_ID = "49095607";

// 利用するHubSpotフォームのGUID（AI導入支援 専用フォーム）。
// 送信するフィールド(email/firstname/company/phone/message)が
// 対象フォームに存在しないと送信エラーになる点に注意。
export const HUBSPOT_FORM_ID = "35ece759-a3f0-4a7b-87ef-2aeca79d107c";

// 資料ダウンロード専用フォーム。
//
// 上記の共通フォームは 診断／資料DL／相談／お問い合わせ の4ページで使い回している。
// HubSpotの「シンプルワークフロー」はフォーム単位で発火するため、
// 共通フォームにフォローアップメールを付けると、資料を請求していない人にも
// 資料が届いてしまう。資料DLだけは別フォームに分ける。
//
// HubSpotで下記のフィールドを持つフォームを作成し、GUIDをここに設定すること。
//   email / firstname / company / phone / message
// （送信するフィールドが対象フォームに存在しないと送信自体が失敗する）
//
// 未設定のあいだは共通フォームに送る。リードは取れるが、
// 資料の自動送付は動かない（担当者が手で送る必要がある）。
export const HUBSPOT_DOWNLOAD_FORM_ID = "010e614e-67ce-4903-a961-9e3553d3cbae";

// 以下は入口ごとに分けるためのフォーム。空のあいだは共通フォームに送る
// （リードは取れるが、通知の件名で入口を区別できない）。
//
// 共通フォームのHubSpot上の名前が「無料診断フォーム」なので、
// 相談から送っても資料請求から送っても通知はすべて「無料診断フォーム」になる。
// HubSpotで下記のフィールドを持つフォームを作り、GUIDをここに入れること。
//   email / firstname / company / phone / message

// 無料相談（/consult）
export const HUBSPOT_CONSULT_FORM_ID = "f9d2cbcb-74c0-435a-b13d-3fb3a6191be7";

// ホワイトペーパー「個人AIから組織AIへ」（/whitepaper）
//
// /download（契約条件の比較表）とは請求する動機が違うので、フォームを分ける。
// 分けておかないと、どちらを請求したリードなのかが通知でも一覧でも区別できない。
//
// 5フィールド（email / firstname / company / phone / message）が揃っていることは
// 実送信で確認済み。存在しないフィールドを送ると INVALID_METADATA_FIELD で
// 400になるため、200が返れば揃っている。
export const HUBSPOT_WHITEPAPER_FORM_ID =
  "1a141c0d-6dbe-40e0-9845-3dd885bd5917";

// 資料「AI導入は何を変えたのか 事例サーベイ」（/whitepaper/ai-case-studies）
//
// ホワイトペーパーは資料ごとにフォームを分ける。
//
// 5フィールドが揃っていることは、不正なメールアドレスでの送信が200で
// 返ることで確認済み（欠けていれば INVALID_METADATA_FIELD で400）。
// その送信でコンタクトが作られていないことも確認した。
export const HUBSPOT_CASE_STUDIES_FORM_ID =
  "be15295f-7276-43ce-ad6f-a6d3b0df2464";

// 無料AI診断（/diagnosis）
// 共通フォーム（HUBSPOT_FORM_ID）と同一。HubSpot上の名前が「無料診断フォーム」で、
// 相談と資料請求が別フォームに分かれた結果、これは診断の専用フォームになった。
export const HUBSPOT_DIAGNOSIS_FORM_ID = "35ece759-a3f0-4a7b-87ef-2aeca79d107c";

// サービス資料の請求（/document）
export const HUBSPOT_SERVICE_DOC_FORM_ID =
  "36e21d1e-8809-4a2c-8f2d-7374ee011b94";

// コーポレートサイトの無料相談・お問い合わせ（/contact）。HubSpot 上の名前は「問い合わせフォーム」。
// AI Media 側の /consult（HUBSPOT_CONSULT_FORM_ID）とは分けている。
// 項目は lastname / firstname / company / email / phone / message。
// 2026-10-09 にテスト送信で受信を確認済み。
export const HUBSPOT_CONTACT_FORM_ID = "8c2bfe1d-bc7d-4531-908c-687d6e25fc20";

// fields: { email, firstname, company, phone, message } のような HubSpot プロパティ名→値
export async function submitHubSpotForm({
  fields,
  pageName,
  formId = HUBSPOT_FORM_ID,
}) {
  const url = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${formId}`;

  // hutk（hubspotutk クッキー）を渡すと、この送信がトラッキング済みの訪問
  // セッションに紐づき、流入元・閲覧ページ履歴がコンタクトに引き継がれる。
  // 渡さないと HubSpot は流入元を判定できず、すべて OFFLINE / DIRECT_TRAFFIC
  // として記録される。クッキーが無い場合はキー自体を送らない（空文字は不正）。
  const hutk = getHubSpotUtk();

  const payload = {
    fields: Object.entries(fields)
      .filter(([, v]) => v != null && String(v).trim() !== "")
      .map(([name, value]) => ({ name, value: String(value) })),
    context: {
      ...(hutk ? { hutk } : {}),
      pageUri: typeof window !== "undefined" ? window.location.href : "",
      pageName:
        pageName || (typeof document !== "undefined" ? document.title : ""),
    },
  };

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HubSpot submit failed: ${res.status} ${text}`);
  }
  return res.json().catch(() => ({}));
}

// ============================================================
// 自動返信（WordPress経由）
// ============================================================
//
// HubSpotのフォーム自動返信は有料プランの機能で使えないため、
// WordPress側のエンドポイントから送る。
// HubSpotへの送信（リード記録・流入元）とは独立していて、
// こちらが失敗してもフォーム自体は成立させる。

const LEAD_ENDPOINT =
  process.env.REACT_APP_LEAD_ENDPOINT || "/wp-json/leaps/v1/lead";

/**
 * 自動返信と社内通知を依頼する。
 *
 * @param {string} kind download / document / consult / diagnosis
 * @param {object} fields { email, name, company, tel, message }
 */
export async function sendLeadMail(kind, fields = {}) {
  try {
    const res = await fetch(LEAD_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        kind,
        email: fields.email || "",
        name: fields.name || "",
        company: fields.company || "",
        tel: fields.tel || "",
        message: fields.message || "",
        // ボットよけ。人が触らない欄なので常に空で送る。
        website: "",
      }),
    });
    if (!res.ok) {
      console.warn("[lead] mail endpoint returned", res.status);
    }
  } catch (err) {
    // 自動返信が落ちてもフォームは成立させる。
    console.warn("[lead] mail endpoint failed", err);
  }
}
